// RevenueCat native SDK wrapper (iOS + Android).
//
// Design contract
// ---------------
// * Only ever runs on native iOS OR native Android. Web hits an
//   early-return guard and NEVER loads the plugin. This keeps our web
//   bundle free of native IAP UI and lets Stripe stay the only web
//   billing path.
// * Each platform has its own PUBLIC key in .env:
//     REACT_APP_REVENUECAT_IOS_KEY     (starts with "appl_...")
//     REACT_APP_REVENUECAT_ANDROID_KEY (starts with "goog_...")
//   Both apps in the RevenueCat dashboard share the SAME entitlement
//   ("premium"), so /api/revenuecat/webhook grants access identically
//   regardless of which store the purchase originated from.
// * All error surfaces are string-safe (we JSON.stringify plugin errors
//   before showing them) — Apple / Google review reviewers see clean
//   error text.
// * `configureRevenueCat(userId)` is idempotent — safe to call from
//   multiple mount points; a module-level `_configured` flag debounces.
// * `premium` is the ONLY entitlement we ever check. Product IDs can
//   change (yearly discount, promo product) — the entitlement is stable.
//
// STATIC IMPORT — CRITICAL
// ------------------------
// RevenueCat staff explicitly warn against dynamic import of this
// plugin: on Capacitor 8.3+ the plugin proxy is unintentionally
// `thenable`, causing `await import(...)` (and subsequently
// `Purchases.configure(...)`) to hang forever with no logs. We import
// the SDK statically so the plugin proxy is created eagerly and Web
// bundles simply never call it (the `isRevenueCatEnabled` guard blocks
// Web + Android at the entry point).
//   Source: RevenueCat Community thread 7766 (staff reply, 2026-07-27)

import { Purchases, LOG_LEVEL } from "@revenuecat/purchases-capacitor";
import { isNativeApp, nativePlatform } from "@/lib/native";
import { getPostbackId, configurePostback } from "@/lib/postback";
import { getAppsFlyerUID } from "@/lib/appsflyer";

const RC_KEY_IOS = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const RC_KEY_ANDROID = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";
let _configured = false;

/** Return the correct RevenueCat public key for the current runtime.
 *  RevenueCat requires separate iOS + Android public keys because each
 *  app is registered separately in the dashboard. */
function apiKeyForPlatform() {
  const p = nativePlatform();
  if (p === "ios") return RC_KEY_IOS;
  if (p === "android") return RC_KEY_ANDROID;
  return "";
}

/** Native iOS OR Android with a valid platform-specific key. Web +
 *  browser preview always return false so Stripe stays the only web
 *  billing path. */
export function isRevenueCatEnabled() {
  if (!isNativeApp()) return false;
  return !!apiKeyForPlatform();
}

/**
 * Configure the SDK. Must be called after our own login has resolved
 * so RevenueCat is scoped to the correct app-user-id — otherwise
 * receipts get stranded on an anonymous account.
 *
 * Wrapped with an internal timeout so a hung native bridge (missing
 * plugin registration, sandbox propagation delay) surfaces as a
 * proper error instead of an infinite paywall spinner.
 */
export async function configureRevenueCat(userId) {
  if (!isRevenueCatEnabled()) return;
  if (_configured) return;
  if (!userId) return;

  if (process.env.NODE_ENV !== "production") {
    try { await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }); } catch {}
  }

  const configurePromise = Purchases.configure({
    apiKey: apiKeyForPlatform(),
    appUserID: String(userId),
  });
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("configure_timeout")), 6000)
  );
  await Promise.race([configurePromise, timeout]);
  _configured = true;

  // Enable Apple Search Ads (AdServices) attribution — iOS 14.3+ only.
  // RC calls Apple's AdServices API server-side with the token so ASA
  // campaign / keyword / creative ↔ subscription revenue joins appear
  // in the RevenueCat "Attribution → Apple Search Ads" dashboard.
  // Docs: https://www.revenuecat.com/docs/integrations/attribution/apple-search-ads
  // The method is a no-op on Android and older iOS; safe to always call
  // on native. RC dashboard integration ("Project Settings →
  // Integrations → Apple Search Ads") must also be toggled ON.
  if (nativePlatform() === "ios") {
    try {
      await Purchases.enableAdServicesAttributionTokenCollection();
    } catch (e) {
      // Non-fatal — attribution is a "nice to have" analytics signal,
      // never block the paywall / purchase flow because it failed.
      console.warn("[RevenueCat] enableAdServicesAttributionTokenCollection failed", e);
    }
  }

  // Postback MMP: sync the install-scoped `postbackId` into
  // RevenueCat as a subscriber attribute. Every future INITIAL_
  // PURCHASE / RENEWAL / CANCELLATION / EXPIRATION webhook RC fires
  // will include this attribute, which is the ONLY way Postback's
  // /v1/integrations/revenuecat/webhooks/{appId} endpoint can join
  // subscription revenue back to the original Postback install
  // (docs: https://postback.sh/docs/revenuecat, step 3).
  //
  // ⚠️ Attribute name MUST be `postbackId` (no `$` prefix). Postback's
  // webhook parser looks for that exact key. RevenueCat's `$`-prefixed
  // keys are reserved system attributes (`$email`, `$displayName`,
  // etc.) and are namespaced separately — using `$postbackId` would
  // send the attribute but Postback's join step would silently miss
  // every event.
  //
  // Runs on iOS AND Android — RC's Android SDK supports the same
  // subscriber attribute API. If Postback's native plugin isn't
  // registered (older builds, dev), configurePostback + getPostbackId
  // both no-op and this whole block reduces to a cheap try/catch —
  // never blocks the real RC configure flow.
  try {
    await configurePostback(userId);
    const pbId = await getPostbackId();
    if (pbId) {
      await Purchases.setAttributes({ attributes: { postbackId: pbId } });
    }
  } catch (e) {
    console.warn("[Postback] RC attribute sync failed", e);
  }

  // AppsFlyer UID → RevenueCat subscriber attribute. AppsFlyer's
  // dashboard has a native RevenueCat integration (Integrations →
  // RevenueCat) that joins subscription events back to AppsFlyer
  // installs using an attribute named exactly `appsflyer_id`. Also
  // set the reserved RC key `$appsflyerID` so RC's own AppsFlyer
  // dashboard integration works (belt-and-braces, no cost). Wrapped
  // in its own try/catch so a slow AppsFlyer init never blocks the
  // paywall from opening.
  try {
    const afId = await getAppsFlyerUID();
    if (afId) {
      await Purchases.setAttributes({
        attributes: {
          appsflyer_id: afId,
          $appsflyerID: afId,
        },
      });
    }
  } catch (e) {
    console.warn("[AppsFlyer] RC attribute sync failed", e);
  }
}

/**
 * True if the customerInfo shows the user has ANY active entitlement.
 *
 * Calofay ships a single entitlement tier — historically we hard-coded
 * the check against `entitlements.active.premium.isActive`, but that
 * silently returned `false` when:
 *   1. The RC dashboard entitlement is named anything other than
 *      "premium" (e.g. "pro", "calofay_premium").
 *   2. Fresh Apple purchases where RC v2 API has propagated the
 *      subscription but not yet the entitlement snapshot with the
 *      expected key (~5-30 s lag).
 * When that flipped to false after a real purchase, `success` came
 * back false, `confirmSubscriptionActive` polled reconcile in vain,
 * and the user saw "verification delayed" — the "nothing happens"
 * symptom on TestFlight. Trust ANY active entitlement instead — the
 * SDK's `active` map already excludes expired/revoked entries. */
function _anyActiveEntitlement(customerInfo) {
  const active = customerInfo?.entitlements?.active;
  if (!active || typeof active !== "object") return false;
  return Object.values(active).some((e) => e?.isActive === true);
}

/**
 * True if the current user has any active entitlement (Calofay only
 * exposes `premium`, but we accept any tier for forward-compat).
 * Uses locally cached CustomerInfo — no network round-trip needed
 * for a UI gate. Backend remains authoritative for protected API
 * calls (webhook + `/api/revenuecat/reconcile`).
 */
export async function hasPremiumEntitlement() {
  if (!isRevenueCatEnabled()) return false;
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return _anyActiveEntitlement(customerInfo);
  } catch (e) {
    console.warn("[RevenueCat] getCustomerInfo failed", e);
    return false;
  }
}

/**
 * Fetch monthly + yearly packages from the "current" offering.
 * Returns `{ packages, reason }` — `reason` is set when we return an
 * empty list so the UI can show a specific error instead of a generic
 * "no plans" state (helps distinguish App Store Connect misconfig
 * from network / sandbox propagation issues).
 */
export async function getOfferingPackages() {
  if (!isRevenueCatEnabled()) return { packages: [], reason: "not_native" };
  try {
    const { current } = await Purchases.getOfferings();
    if (!current) return { packages: [], reason: "no_current_offering" };
    // Web sells Quarterly + Annual. `threeMonth` and `annual` are the
    // RevenueCat predefined package types that map cleanly onto those.
    // Fall back to scanning `availablePackages` for custom identifiers
    // in case the offering uses non-standard names.
    const preferred = [current.threeMonth, current.annual].filter(Boolean);
    const packages =
      preferred.length >= 2
        ? preferred
        : current.availablePackages && current.availablePackages.length
          ? current.availablePackages
          : preferred;
    if (!packages.length) return { packages: [], reason: "offering_has_no_packages" };
    return { packages, reason: null };
  } catch (e) {
    console.warn("[RevenueCat] getOfferings failed", e);
    return { packages: [], reason: `error:${e?.message || "unknown"}` };
  }
}

/**
 * Kick off the Apple StoreKit purchase sheet. Resolves with
 * `{ success: true }` on successful purchase, `{ success: false, cancelled: true }`
 * when the user backs out (Apple's sheet is a modal), or throws for
 * network / receipt errors so the caller can toast them.
 */
export async function purchasePackage(pkg) {
  if (!isRevenueCatEnabled()) throw new Error("RevenueCat unavailable");
  if (!pkg) throw new Error("Package required");

  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    const active = _anyActiveEntitlement(customerInfo);
    return { success: active };
  } catch (e) {
    // The plugin throws with `userCancelled: true` on Apple's Cancel tap.
    if (e?.userCancelled || e?.code === "1" || e?.code === 1) {
      return { success: false, cancelled: true };
    }
    throw e;
  }
}

/**
 * Restore previous purchases — Apple REQUIRES a Restore Purchases
 * button in every app that sells subscriptions (Guideline 3.1.1).
 */
export async function restorePurchases() {
  if (!isRevenueCatEnabled()) return { success: false };
  const { customerInfo } = await Purchases.restorePurchases();
  const active = _anyActiveEntitlement(customerInfo);
  return { success: active };
}

/**
 * Force RevenueCat to push the local Apple receipt to its servers,
 * which then hits our webhook. Unlike `restorePurchases`, this is
 * silent — no user-visible sheet, no "already an active subscription"
 * toast on the user's side. We call it during the redeem poll to
 * automate the exact step a user was manually doing after a promo-code
 * purchase ("tap Restore to move forward"). `syncPurchases` is the RC
 * v2 API method for exactly this case (see App Store Connect Offer
 * Code redemption flow docs).
 */
export async function syncPurchases() {
  if (!isRevenueCatEnabled()) return { success: false };
  try {
    await Purchases.syncPurchases();
    const { customerInfo } = await Purchases.getCustomerInfo();
    return { success: _anyActiveEntitlement(customerInfo) };
  } catch (e) {
    console.warn("[RevenueCat] syncPurchases failed", e);
    return { success: false };
  }
}

/**
 * Subscribe to RC's local `customerInfo` push updates. Fires the
 * moment RC detects a change in the entitlement snapshot (e.g. right
 * after Apple's redemption sheet closes with a successful purchase),
 * BEFORE our webhook has run and BEFORE any poll iteration. Lets the
 * redeem flow short-circuit the polling and call onSuccess as fast as
 * the SDK knows. Returns the callback id so we can remove the listener
 * on unmount / after success.
 */
export async function addCustomerInfoUpdateListener(callback) {
  if (!isRevenueCatEnabled()) return null;
  try {
    const listenerId = await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
      try { callback && callback(customerInfo, _anyActiveEntitlement(customerInfo)); }
      catch (e) { console.warn("[RevenueCat] listener callback threw", e); }
    });
    return listenerId;
  } catch (e) {
    console.warn("[RevenueCat] addCustomerInfoUpdateListener failed", e);
    return null;
  }
}

/** Detach a previously-registered customer-info listener. */
export async function removeCustomerInfoUpdateListener(listenerId) {
  if (!isRevenueCatEnabled() || !listenerId) return;
  try {
    await Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: listenerId });
  } catch (e) {
    console.warn("[RevenueCat] removeCustomerInfoUpdateListener failed", e);
  }
}

/**
 * Set a typed subscriber attribute on the current user's RevenueCat
 * customer record. RC then attaches this attribute to EVERY future
 * webhook event for the user (INITIAL_PURCHASE, RENEWAL, CANCELLATION,
 * EXPIRATION…) — this is the community-recommended attribution
 * primitive that DOESN'T depend on the raw Apple offer_code string
 * (StoreKit doesn't always return it reliably).
 *
 * We call this from the paywall the moment we detect an affiliate
 * cookie / URL param so a downstream purchase is credited to the
 * right creator even months later on renewal.
 */
export async function setAttributes(attrs) {
  if (!isRevenueCatEnabled() || !attrs) return;
  try {
    await Purchases.setAttributes({ attributes: attrs });
  } catch (e) {
    console.warn("[RevenueCat] setAttributes failed", e);
  }
}

/**
 * Present Apple's built-in code redemption sheet so users can redeem
 * Apple Offer Codes / promo codes issued from App Store Connect.
 * Available in RevenueCat SDK v5+; a no-op on non-iOS platforms.
 */
export async function presentCodeRedemptionSheet() {
  if (!isRevenueCatEnabled()) return;
  try {
    await Purchases.presentCodeRedemptionSheet();
  } catch (e) {
    console.warn("[RevenueCat] presentCodeRedemptionSheet failed", e);
  }
}

/** Called from logout — un-scopes future purchases from this user. */
export async function logOutRevenueCat() {
  if (!isRevenueCatEnabled() || !_configured) return;
  try {
    await Purchases.logOut();
    _configured = false;
  } catch (e) {
    console.warn("[RevenueCat] logOut failed", e);
  }
}
